import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { productAnalytics } from '../feature-flags';
import { FirebaseAuthService } from '../providers/firebase-auth.service';
import { AnalyticsService } from './analytics.service';

// What a signed-in user did, as the trackProductEvents function accepts it (its allowlist): the
// steps of the render flow, not every click. What happened on the backend — the preview ready, the
// payment, the videos — the function reads off the model's own document; these are the user's side.
export type ProductEventName =
  | 'page_viewed'
  | 'upload_selected'
  | 'upload_rejected'
  | 'missing_textures_shown'
  | 'upload_started'
  | 'upload_completed'
  | 'upload_failed'
  | 'preview_viewed'
  | 'unlock_opened'
  | 'purchase_clicked'
  | 'checkout_failed'
  | 'checkout_returned'
  | 'videos_viewed'
  | 'download_requested'
  | 'download_failed'
  | 'help_opened'
  | 'feedback_sent'
  | 'sample_opened'
  | 'model_deleted'
  | 'leave_dialog_shown';

// Codes, counts and our own ids only: never a file or model name, free text, an address, a Stripe id
// or an error's message. Undefined and null values are left out.
export type ProductEventParams = Record<string, string | number | boolean | null | undefined>;

export interface ProductEventData {
  // The model's backend id (its Firestore document), for a user's own upload; none for a sample.
  modelId?: string;
  params?: ProductEventParams;
}

interface ProductEvent {
  id: string;
  name: ProductEventName;
  at: number;
  route?: string;
  modelId?: string;
  params?: Record<string, string | number | boolean>;
}

interface Queued {
  event: ProductEvent;
  // Sends tried so far: a batch the network lost goes once more, then it is dropped.
  tries: number;
}

// The function's limits (trackProductEvents): a batch, a params object, a value.
const MAX_BATCH = 50;
const MAX_PARAMS = 12;
const MAX_STRING = 120;
const MAX_ROUTE = 120;
const MAX_MODEL_ID = 300;
const PARAM_KEY = /^[a-z][a-z0-9_]{0,31}$/;
// When the queue goes out: this many events, or this long after the first one waiting.
const FLUSH_AT = 20;
const FLUSH_AFTER_MS = 5000;
// What waits at most while sends fail (a long offline spell): the oldest go first.
const MAX_QUEUE = 200;
// The ID token kept for the send on the way out: renewed this long before it expires, and not used
// in its last minute.
const TOKEN_RENEW_MS = 10 * 60 * 1000;
const TOKEN_MARGIN_MS = 60 * 1000;

// Our own product analytics (feature flag `productAnalytics`, owner 2026-11-03): what signed-in users
// do in the render flow, sent in batches to the trackProductEvents function, which keeps it in
// Firestore for /admin/analytics. Google Analytics counts visits; this answers who did what with
// which model.
//
// - Signed-in users only, and never on /admin. Where the cookie banner exists (production,
//   gaMeasurementId) only after the visitor accepts analytics there — the same choice, not a second
//   one; without the banner (dev, localdev) there is no choice to follow. Withdrawing, signing out or
//   another account signing in drops whatever still waits.
// - Nothing is kept in the browser: the session id lives in this tab's memory, the queue too. A tab
//   closed with events waiting sends them once more on its way out (keepalive), or loses them.
// - Page views carry the route's pattern (/render/preview/:modelId), never the address.
// - Never in the caller's way: track() returns at once and nothing here throws; a send that fails is
//   tried once more and then forgotten.
@Injectable({ providedIn: 'root' })
export class ProductAnalyticsService {
  private readonly url = `https://us-central1-${environment.firebaseConfig.projectId}.cloudfunctions.net/trackProductEvents`;
  private sessionId = randomId(24);
  private queue: Queued[] = [];
  // The account the queue was made for: another one starts it over.
  private queueUid: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private sending: Promise<void> | null = null;
  // The last ID token, for the send on the way out, which cannot wait for one.
  private token: { uid: string; value: string; expires: number } | null = null;
  // The route pattern last counted as a page view, and one seen before the user was known.
  private lastPage: string | null = null;
  private pendingPage: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly analytics: AnalyticsService,
  ) {
    if (!productAnalytics) {
      return;
    }
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.pageView());
    // Signed in (again): a page opened before Firebase knew the user is counted now.
    this.firebaseAuth.firebaseUser.subscribe(() => {
      if (this.pendingPage) {
        const page = this.pendingPage;
        this.pendingPage = null;
        this.lastPage = null;
        this.pageView(page);
      }
    });
    this.firebaseAuth.notLoggedUserSubscription.subscribe(() => this.drop());
    this.analytics.consent$.subscribe(() => {
      if (!this.consented()) {
        this.drop();
      }
    });
    this.zone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.sendOnTheWayOut();
        }
      });
      window.addEventListener('pagehide', () => this.sendOnTheWayOut());
    });
  }

  public track(name: ProductEventName, data: ProductEventData = {}): void {
    try {
      const uid = this.uid();
      if (!uid) {
        return;
      }
      const route = this.routePattern();
      if (isAdmin(route)) {
        return;
      }
      if (this.queueUid !== uid) {
        this.drop();
        this.queueUid = uid;
      }
      const event: ProductEvent = { id: randomId(20), name, at: Date.now(), route: route.slice(0, MAX_ROUTE) };
      const modelId = data.modelId && !data.modelId.includes('/') && data.modelId.length <= MAX_MODEL_ID ? data.modelId : undefined;
      if (modelId) {
        event.modelId = modelId;
      }
      const params = cleanParams(data.params);
      if (params) {
        event.params = params;
      }
      this.queue.push({ event, tries: 0 });
      if (this.queue.length > MAX_QUEUE) {
        this.queue.splice(0, this.queue.length - MAX_QUEUE);
      }
      this.refreshToken();
      if (this.queue.length >= FLUSH_AT) {
        this.flush();
      } else {
        this.schedule();
      }
    } catch (error) {
      console.warn('Product analytics: could not record', name, error);
    }
  }

  // Everything waiting, sent now: before the page leaves for Stripe. Resolves when it is sent or
  // after `timeoutMs`, whichever comes first — the caller goes on either way.
  public flushNow(timeoutMs = 1500): Promise<void> {
    if (!productAnalytics) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const timeoutId = setTimeout(resolve, timeoutMs);
      this.flush().then(() => {
        clearTimeout(timeoutId);
        resolve();
      });
    });
  }

  // The signed-in user whose steps may be recorded right now, or null.
  private uid(): string | null {
    if (!productAnalytics || !this.consented()) {
      return null;
    }
    return this.firebaseAuth.currentUser?.uid || null;
  }

  private consented(): boolean {
    return !this.analytics.enabled || this.analytics.consent$.getValue() === 'granted';
  }

  private pageView(known?: string): void {
    const route = known || this.routePattern();
    if (route === this.lastPage) {
      return;
    }
    if (!this.firebaseAuth.currentUser) {
      // Firebase restores the session a moment after the first page: count it then.
      this.pendingPage = isAdmin(route) ? null : route;
      return;
    }
    this.lastPage = route;
    this.track('page_viewed');
  }

  private schedule(): void {
    if (this.timer) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => {
        this.timer = null;
        this.flush();
      }, FLUSH_AFTER_MS);
    });
  }

  // One batch at a time; whatever came in meanwhile goes next.
  private flush(): Promise<void> {
    if (this.sending) {
      return this.sending.then(() => this.flush());
    }
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.queue.length) {
      return Promise.resolve();
    }
    this.sending = this.zone.runOutsideAngular(() => this.sendBatch()).finally(() => {
      this.sending = null;
      if (this.queue.length) {
        this.schedule();
      }
    });
    return this.sending;
  }

  private async sendBatch(): Promise<void> {
    const uid = this.queueUid;
    const user = this.firebaseAuth.currentUser;
    if (!uid || !user || user.uid !== uid) {
      this.drop();
      return;
    }
    const batch = this.queue.splice(0, MAX_BATCH);
    try {
      const value = await user.getIdToken();
      this.token = { uid, value, expires: tokenExpiry(value) };
      const response = await this.post(value, batch.map((item) => item.event));
      if (response.ok) {
        return;
      }
      // Refused as it is (400, 403): sending it again changes nothing.
      if (response.status < 500 && response.status !== 401 && response.status !== 429) {
        console.warn(`Product analytics: ${batch.length} events refused (${response.status})`);
        return;
      }
      this.retry(uid, batch);
    } catch {
      this.retry(uid, batch);
    }
  }

  // The page is going (hidden, closed, or off to another site): what waits goes now, with the token
  // already in hand. keepalive lets the request outlive the page.
  private sendOnTheWayOut(): void {
    try {
      const uid = this.queueUid;
      const token = this.token;
      if (!this.queue.length || !uid || !token || token.uid !== uid || token.expires - Date.now() < TOKEN_MARGIN_MS) {
        return;
      }
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      const batch = this.queue.splice(0, MAX_BATCH);
      this.post(token.value, batch.map((item) => item.event)).catch(() => undefined);
    } catch {
      // The page is leaving: nothing left to do about it.
    }
  }

  private post(token: string, events: ProductEvent[]): Promise<Response> {
    return fetch(this.url, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: this.sessionId, events }),
    });
  }

  // Back to the front of the queue, once, while the same user is signed in.
  private retry(uid: string, batch: Queued[]): void {
    if (this.queueUid !== uid) {
      return;
    }
    const again = batch.filter((item) => item.tries < 1).map((item) => ({ ...item, tries: item.tries + 1 }));
    this.queue.unshift(...again);
    if (this.queue.length > MAX_QUEUE) {
      this.queue.splice(0, this.queue.length - MAX_QUEUE);
    }
  }

  // A token in hand for the send on the way out; Firebase hands out its cached one until it is due.
  private refreshToken(): void {
    const user = this.firebaseAuth.currentUser;
    if (!user || (this.token?.uid === user.uid && this.token.expires - Date.now() > TOKEN_RENEW_MS)) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      user.getIdToken().then(
        (value) => {
          if (this.firebaseAuth.currentUser?.uid === user.uid) {
            this.token = { uid: user.uid, value, expires: tokenExpiry(value) };
          }
        },
        () => undefined,
      );
    });
  }

  // Signed out, another account, or consent withdrawn: nothing that waits is sent, and the next
  // events start a new session.
  private drop(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const hadSomeone = this.queueUid !== null;
    this.queue = [];
    this.queueUid = null;
    this.token = null;
    this.pendingPage = null;
    if (hadSomeone) {
      this.sessionId = randomId(24);
      this.lastPage = null;
    }
  }

  // The matched routes' own paths, joined: '/render/preview/:modelId' for /render/preview/abc123?x=1
  // (as AnalyticsService counts pages).
  private routePattern(): string {
    const parts: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) {
        parts.push(path);
      }
      route = route.firstChild;
    }
    return '/' + parts.join('/');
  }
}

// A stable code for an error, for a `reason` param: an HTTP status, a Firebase code
// ('storage/canceled'), or our own error classes' names — never the message, which may carry a file
// name or an address.
export function productErrorCode(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return `http_${error.status}`;
  }
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code === 'string' && /^[A-Za-z0-9/_.-]{1,60}$/.test(code)) {
    return code;
  }
  const name = (error as Error | null)?.name;
  if (name && name !== 'Error' && /^\w{1,60}$/.test(name)) {
    return name;
  }
  return 'unknown';
}

// A size for a param: megabytes, one decimal.
export function productMegabytes(bytes: number): number {
  return Math.round((bytes / (1024 * 1024)) * 10) / 10;
}

function isAdmin(route: string): boolean {
  return route === '/admin' || route.startsWith('/admin/');
}

function cleanParams(params: ProductEventParams | undefined): Record<string, string | number | boolean> | undefined {
  if (!params) {
    return undefined;
  }
  const clean: Record<string, string | number | boolean> = {};
  let count = 0;
  for (const [key, value] of Object.entries(params)) {
    if (count >= MAX_PARAMS || !PARAM_KEY.test(key) || value === undefined || value === null) {
      continue;
    }
    if (typeof value === 'string') {
      clean[key] = value.slice(0, MAX_STRING);
    } else if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        continue;
      }
      clean[key] = value;
    } else if (typeof value === 'boolean') {
      clean[key] = value;
    } else {
      continue;
    }
    count++;
  }
  return count ? clean : undefined;
}

// When an ID token expires (its `exp`), or 0 when it cannot be read.
function tokenExpiry(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function randomId(length: number): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
}
